import type { CandidateStatus, ConsentExpiryInfo, GdprStatus } from '@/types/candidates';
import { consentExpiryRowTone } from './consent-expiry';

// HW-1056: Anonymise deletes the candidate, so every record still here holds
// data — a "[anonymised]" card from before is revoked and offered it again.
type ConsentSubject = {
  gdprStatus: GdprStatus;
  status: CandidateStatus;
  /** Present on the profile, not in the list; the backend counts it as hired. */
  employeeId?: string | null;
};

/** Hired the way the backend counts it: status Hired, or linked to an employee. */
function isHired(c: ConsentSubject): boolean {
  return c.status === 'HIRED' || Boolean(c.employeeId);
}

/**
 * Which consent actions the GDPR status card offers [HW-1055]. The actions
 * follow the status, so none of them can lead into a refusal:
 * - Pending / Expired: ask for consent (email) or record it offline — Expired
 *   keeps both, or a consent that ended after a year could not be renewed —
 *   and Anonymise, for a candidate who never consents;
 * - Approved: Revoke;
 * - Revoked: Anonymise only — a candidate who refused is never asked again.
 * A hired candidate is an employee now: no Revoke, no Anonymise. ("Hired"
 * matches the backend only when `employeeId` is passed — the profile has it.)
 */
export interface ConsentCardActions {
  request: boolean;
  revoke: boolean;
  anonymise: boolean;
}

export function consentCardActions(c: ConsentSubject): ConsentCardActions {
  const none = { request: false, revoke: false, anonymise: false };
  const hired = isHired(c);
  switch (c.gdprStatus) {
    case 'PENDING':
    case 'EXPIRED':
      return { request: true, revoke: false, anonymise: !hired };
    case 'APPROVED':
      return { ...none, revoke: !hired };
    case 'REVOKED':
      return { ...none, anonymise: !hired };
    default:
      return none;
  }
}

/**
 * The backend's own message when it refused because the candidate revoked
 * consent (409 CONSENT_REVOKED) [HW-1055]; null for any other error, which
 * keeps the caller's generic wording.
 */
export function consentRevokedMessage(err: unknown): string | null {
  const data = (err as { response?: { data?: { code?: unknown; message?: unknown } } })?.response
    ?.data;
  if (data?.code !== 'CONSENT_REVOKED') return null;
  return typeof data.message === 'string' && data.message.trim()
    ? data.message
    : 'This candidate revoked consent';
}

/** "Send" for a first consent email, "Resend" once one went out. */
export function consentEmailLabel(lastRequestedAt: string | null): string {
  return lastRequestedAt ? 'Resend consent email' : 'Send consent email';
}

/**
 * Candidate list: a candidate who revoked consent and is not anonymised yet
 * stands out, so the recruiter does not forget to anonymise them [HW-1055].
 * Archiving does not count as anonymising; a hired candidate is not
 * anonymised on the candidate side, so they are not flagged.
 */
export function awaitsAnonymisation(c: ConsentSubject): boolean {
  return c.gdprStatus === 'REVOKED' && !isHired(c);
}

/**
 * A candidate who revoked consent is read-only until anonymised [HW-1055]: the
 * backend refuses every change but archiving, closing an assignment as
 * Rejected, cancelling an interview and the erasure itself, so the profile
 * offers none of the others. `undefined` while the candidate loads counts as
 * not locked — the controls then follow the permission alone.
 */
export function isConsentLocked(c: { gdprStatus: GdprStatus } | null | undefined): boolean {
  return c?.gdprStatus === 'REVOKED';
}

/** Why a control is off on a candidate who revoked consent [HW-1055]. */
export const CONSENT_LOCKED_HINT = 'Consent revoked — read-only until anonymised';

/** Row tint in the candidate list: awaiting anonymisation, then consent expiry [HW-1023]. */
export function consentRowTone(c: ConsentSubject & { consentExpiry: ConsentExpiryInfo | null }) {
  if (awaitsAnonymisation(c)) return 'bg-destructive/10';
  return consentExpiryRowTone(c.consentExpiry);
}
